import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@headlessui/react';
import { Button, Text } from '@tremor/react';
import { XMarkIcon, ChevronLeftIcon, ChevronRightIcon, CheckIcon } from '@heroicons/react/24/outline';
import TrSourcesTab from './TrSourcesTab';
import TrReportTab from './TrReportTab';
import TrInsightsTab from './TrInsightsTab';

/** Full-screen "Relatório de Tendências" wizard: Sources -> Report -> Insights. */
const STEPS = [
    { key: 'sources', titleKey: 'TendencyReport.Sources.TabName', Comp: TrSourcesTab },
    { key: 'report', titleKey: 'TendencyReport.Report.TabName', Comp: TrReportTab },
    { key: 'insights', titleKey: 'TendencyReport.Insights.TabName', Comp: TrInsightsTab },
];

function TendencyReportModal({ isOpen, onClose, projectUuid, analysisUuid }) {
    const { t } = useTranslation();
    const [step, setStep] = useState(0);
    const saveRef = useRef(null);

    // Leaving Sources saves whatever the researcher set, so the report never ignores it.
    const goTo = async (next) => {
        if (step === 0 && next !== 0 && saveRef.current) {
            try {
                await saveRef.current();
            } catch (e) {
                // the step's own error handling already surfaced it
            }
        }
        setStep(next);
    };

    const isFirst = step === 0;
    const isLast = step === STEPS.length - 1;
    const StepComp = STEPS[step].Comp;

    const handleClose = () => {
        setStep(0);
        onClose();
    };

    return (
        <Dialog open={isOpen} onClose={handleClose} className="relative z-50">
            <div className="fixed inset-0 bg-black bg-opacity-50" aria-hidden="true" />
            <div className="fixed inset-0">
                <Dialog.Panel className="w-full h-full bg-white flex flex-col">
                    <div className="flex items-center justify-between p-6 border-b">
                        <div>
                            <Dialog.Title className="text-lg font-semibold text-gray-900">{t('TendencyReport.TabName')}</Dialog.Title>
                            <Text className="text-gray-600 mt-1">
                                {t('TendencyReport.Step')} {step + 1}/{STEPS.length} — {t(STEPS[step].titleKey)}
                            </Text>
                        </div>
                        <button onClick={handleClose} className="text-gray-400 hover:text-gray-500" aria-label={t('TendencyReport.Close')}>
                            <XMarkIcon className="h-6 w-6" />
                        </button>
                    </div>

                    <div className="flex items-center justify-center gap-2 py-4 border-b bg-gray-50">
                        {STEPS.map((s, i) => (
                            <button key={s.key} onClick={() => goTo(i)} className="flex items-center gap-2" aria-label={t(s.titleKey)}>
                                <span className={`flex size-7 items-center justify-center rounded-full text-xs font-bold ${
                                    i < step ? 'bg-iavisus-100 text-white' : i === step ? 'bg-iavisus-900 text-white' : 'border-2 border-gray-300 text-gray-400'
                                }`}>
                                    {i < step ? <CheckIcon className="size-4" /> : i + 1}
                                </span>
                                <span className={`hidden md:inline text-xs font-medium ${i === step ? 'text-iavisus-100' : 'text-gray-400'}`}>{t(s.titleKey)}</span>
                                {i < STEPS.length - 1 && <span className="mx-1 text-gray-300">›</span>}
                            </button>
                        ))}
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        <div className="p-8 max-w-4xl mx-auto">
                            {analysisUuid && <StepComp projectUuid={projectUuid} analysisUuid={analysisUuid} saveRef={saveRef} />}
                        </div>
                    </div>

                    <div className="border-t p-6">
                        <div className="max-w-4xl mx-auto flex justify-between items-center">
                            <Button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={isFirst} icon={ChevronLeftIcon} className="bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50">
                                {t('TendencyReport.Back')}
                            </Button>
                            {isLast ? (
                                <Button onClick={handleClose} className="bg-iavisus-100 text-white hover:bg-iavisus-200">{t('TendencyReport.Done')}</Button>
                            ) : (
                                <Button onClick={() => goTo(Math.min(STEPS.length - 1, step + 1))} iconPosition="right" icon={ChevronRightIcon} className="bg-iavisus-100 text-white hover:bg-iavisus-200">
                                    {t('TendencyReport.Next')}
                                </Button>
                            )}
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}

export default TendencyReportModal;
